<template>
  <transition name="slide-fade">
    <div 
      v-if="visible" 
      class="error-notification" 
      :class="notificationClass"
      @mouseenter="pauseAutoClose"
      @mouseleave="resumeAutoClose"
    >
      <!-- 关闭按钮 -->
      <button class="close-btn" @click="handleClose">
        <Icon type="md-close" />
      </button>
      
      <!-- 进度条 -->
      <div class="progress-bar" :style="{ width: progressWidth + '%' }"></div>
      
      <!-- 内容区域 -->
      <div class="notification-content">
        <div class="icon-wrapper">
          <Icon type="ios-warning" />
        </div>
        
        <div class="message-wrapper">
          <div class="title">{{ displayTitle }}</div>
          <div class="description" v-html="description"></div>
        </div>
      </div>
      
      <!-- 操作按钮 -->
      <div class="action-wrapper" v-if="false">
        <button class="ignore-btn" @click="handleIgnore">
          <span class="btn-text">{{ langMap.dashboard_ignoreThisTime[lang] }}</span>
          <span class="btn-hint">({{ langMap.dashboard_ignoreHint[lang] }})</span>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';

export default {
  name: 'ErrorNotification',
  props: {
    title: {
      type: String,
      default: '' 
    },
    description: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'warning', // 'warning' | 'error' | 'emergency'
      validator: (value) => ['warning', 'error', 'emergency'].includes(value)
    },
    duration: {
      type: Number,
      default: 5000 // 自动关闭时间（毫秒）
    },
    robotId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      langMap,
      visible: false,
      progressWidth: 100,
      timer: null,
      progressTimer: null
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    notificationClass() {
      return `notification-${this.type}`;
    },
    displayTitle() {
      if (this.title) return this.title;
      return this.langMap.dashboard_errorNotificationTitle[this.lang];
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.show();
    });
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    show() {
      // 重置状态
      this.progressWidth = 100;
      this.clearTimers();
      this.visible = true;
      this.$nextTick(() => {
        this.startAutoClose();
      });
    },
    startAutoClose() {
      if (this.duration > 0) {
        // 进度条动画
        const interval = 50; // 更新间隔（毫秒）
        const step = (interval / this.duration) * 100;
        
        this.progressTimer = setInterval(() => {
          this.progressWidth -= step;
          if (this.progressWidth <= 0) {
            this.progressWidth = 0;
            this.clearTimers();
            this.handleClose();
          }
        }, interval);
        
        // 自动关闭定时器
        this.timer = setTimeout(() => {
          this.handleClose();
        }, this.duration);
      }
    },
    clearTimers() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    pauseAutoClose() {
      this.clearTimers();
    },
    resumeAutoClose() {
      if (this.progressWidth > 0) {
        this.startAutoClose();
      }
    },
    handleClose() {
      this.clearTimers();
      this.visible = false;
      setTimeout(() => {
        this.$emit('close');
      }, 300); // 等待动画结束
    },
    handleIgnore() {
      this.clearTimers();
      this.visible = false;
      setTimeout(() => {
        this.$emit('ignore', this.robotId);
      }, 300);
    }
  }
}
</script>

<style lang="less" scoped>
.error-notification {
  position: fixed;
  top: 80px;
  right: 20px;
  width: 420px;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.98), rgba(0, 40, 80, 0.95));
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 
              0 0 20px rgba(255, 152, 0, 0.3),
              inset 0 0 20px rgba(255, 152, 0, 0.05);
  border: 2px solid rgba(255, 152, 0, 0.4);
  z-index: 99;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  // 进度条
  .progress-bar {
    position: absolute;
    top: 0;
    left: 0;
    height: 3px;
    background: linear-gradient(90deg, #FF9800, #FFA726, #FF9800);
    transition: width 0.05s linear;
    box-shadow: 0 0 10px rgba(255, 152, 0, 0.8);
  }
  
  // 关闭按钮
  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
    z-index: 10;
    padding: 0;
    outline: none;
    
    /deep/ i {
      font-size: 20px;
      line-height: 1;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 1);
      transform: rotate(90deg);
    }
  }
  
  // 内容区域
  .notification-content {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
    
    .icon-wrapper {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 193, 7, 0.15));
      border: 2px solid rgba(255, 152, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 0 15px rgba(255, 152, 0, 0.4);
      
      /deep/ i {
        font-size: 24px;
        color: #FF9800;
        filter: drop-shadow(0 0 5px rgba(255, 152, 0, 0.8));
        animation: iconPulse 2s ease-in-out infinite;
        line-height: 1;
      }
    }
    
    .message-wrapper {
      flex: 1;
      padding-right: 24px; // 为关闭按钮留空间
      
      .title {
        font-size: 16px;
        font-weight: 600;
        color: #fff;
        margin-bottom: 8px;
        line-height: 1.4;
      }
      
      .description {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.75);
        line-height: 1.6;
      }
    }
  }
  
  // 操作区域
  .action-wrapper {
    display: flex;
    justify-content: center;
    
    .ignore-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 10px 20px;
      background: rgba(255, 152, 0, 0.15);
      border: 1px solid rgba(255, 152, 0, 0.4);
      border-radius: 8px;
      color: #FF9800;
      cursor: pointer;
      transition: all 0.3s ease;
      
      .btn-text {
        font-size: 14px;
        font-weight: 500;
      }
      
      .btn-hint {
        font-size: 11px;
        color: rgba(255, 152, 0, 0.7);
      }
      
      &:hover {
        background: rgba(255, 152, 0, 0.25);
        border-color: rgba(255, 152, 0, 0.6);
        box-shadow: 0 0 15px rgba(255, 152, 0, 0.3);
        transform: translateY(-2px);
      }
      
      &:active {
        transform: translateY(0);
      }
    }
  }

  // 错误和紧急状态的样式覆盖
  &.notification-error,
  &.notification-emergency {
    border-color: rgba(244, 67, 54, 0.5);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 
                0 0 20px rgba(244, 67, 54, 0.4),
                inset 0 0 20px rgba(244, 67, 54, 0.05);

    .progress-bar {
      background: linear-gradient(90deg, #F44336, #EF5350, #F44336);
      box-shadow: 0 0 10px rgba(244, 67, 54, 0.8);
    }

    .notification-content .icon-wrapper {
      background: linear-gradient(135deg, rgba(244, 67, 54, 0.2), rgba(229, 57, 53, 0.15));
      border-color: rgba(244, 67, 54, 0.5);
      box-shadow: 0 0 15px rgba(244, 67, 54, 0.4);
      
      /deep/ i {
        color: #F44336;
        filter: drop-shadow(0 0 5px rgba(244, 67, 54, 0.8));
      }
    }

    .action-wrapper .ignore-btn {
      background: rgba(244, 67, 54, 0.15);
      border-color: rgba(244, 67, 54, 0.4);
      color: #F44336;
      
      .btn-hint {
        color: rgba(244, 67, 54, 0.7);
      }
      
      &:hover {
        background: rgba(244, 67, 54, 0.25);
        border-color: rgba(244, 67, 54, 0.6);
        box-shadow: 0 0 15px rgba(244, 67, 54, 0.3);
      }
    }
  }
}

// 动画
.slide-fade-enter-active {
  transition: all 0.4s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>
